<script setup>
import { computed, ref } from 'vue';
import { useStore } from 'vuex';
import { useToast } from 'vue-toast-notification';
import { useScreenSize } from '@/composables';
// import CarrierPortalEldProviderModal from '@/components/modals/CarrierPortalEldProviderModal.vue';
import EquipmentTable from '@/components/EquipmentTable.vue';
import CarrierWorkflowModalComponent from '@/components/modals/CarrierWorkflowModalComponent.vue';
import EquipmentCard from '@/components/EquipmentCard.vue';
import EquipmentCardSkeleton from '@/components/EquipmentCardSkeleton.vue';
import EldProviderSummary from '@/components/EldProviderSummary.vue';
import Pagination from '@/components/LightPagination.vue';
import TeleportModalWrapperComponent from '@/components/TeleportModalWrapperComponent.vue';
import EquipmentSkeletonTable from '@/components/EquipmentSkeletonTable.vue';

const store = useStore();
const toast = useToast();
const { screenSize, innerHeight } = useScreenSize();

const isCarrierVehiclesResponseLoading = computed(() => {
  return store.state.myLoadsStore.equipmentTableVehicles.isLoading;
});

const generalStatus = computed(
  () => store.state.users.userLoadBoardAccess?.user
);

const carrierVehiclesForCarrier = computed(() => {
  return store.state.myLoadsStore.equipmentTableVehicles.vehicles;
});

const carrierVehiclePagination = computed(() => {
  return store.state.myLoadsStore.equipmentTableVehicles.pagination;
});

const eldProviders = computed(() => {
  return store.state.onboardingPacket.eld_integrations;
});

const eldProvidersNotRevoked = computed(() => {
  return eldProviders.value?.filter((item) => item.revoked_at === null);
});

const isTabletOrMobile = computed(
  () => screenSize.value === 'tablet' || screenSize.value === 'mobile'
);

function onLearnMoreEld() {
  window.open(
    `https://service.highway.com/en/articles/10070268-your-eld-connection-to-highway`,
    '_blank'
  );
}

function onClickEld() {
  const dialog = document.getElementById('dialog');
  dialog?.showModal();
}
// function onNewEldConnection() {
//   const dialog = document.getElementById('dialog');
//   dialog?.showModal();
// }
async function onRefresh(integration) {
  toast?.open({
    message: 'Refreshing connection',
    position: 'top-right',
    duration: 5000,
  });
  await store.dispatch('onboardingPacket/refreshEldProvider', integration.id);
}
async function onDelete(integration) {
  try {
    const removeResponse = await store.dispatch(
      'onboardingPacket/removeEldProvider',
      integration.id
    );

    if ([200, 201, 204].includes(removeResponse.status)) {
      toast?.open({
        message: 'ELD Removed',
        position: 'top-right',
        duration: 5000,
      });

      await store.dispatch(
        'onboardingPacket/getEldIntegrations',
        store.state.users?.currentUser?.company?.id
      );
    }
  } catch (error) {
    console.error('Error revoking ELD provider', error);
  }
}

const selectedVehicle = ref({});

const isCarrierWorkflowModalVisible = ref(false);

const handleResolveIssuesClick = (vehicle) => {
  selectedVehicle.value = {
    id: vehicle.carrier_vehicle_id,
    identifier: vehicle.truck_number || '',
    vin: vehicle.vin || '',
    source: vehicle.source,
    isEldActive: vehicle.is_eld_and_active,
    isEldExempt: vehicle.is_eld_exempt,
    eldExemptCategory: vehicle.eld_exemption_type,
    isUnverified: !vehicle.is_coi,
  };
  isCarrierWorkflowModalVisible.value = true;
};
</script>

<template>
  <div
    v-if="!generalStatus.has_eld_connected_and_active"
    class="mt-4 flex h-full flex-col items-center justify-center gap-6 rounded-md border border-steel-300 py-8 lg:mt-0"
    :style="isTabletOrMobile ? 'height: calc(100dvh - 362px)' : ''"
  >
    <img
      src="@/assets/connect-your-eld.svg"
      alt="Identity Verification"
    />
    <div
      :class="isTabletOrMobile ? 'text-center' : 'flex flex-col items-center'"
    >
      <h1 class="mb-2 text-xl font-medium text-neutral-boldest">
        Connect your ELD
      </h1>
      <p class="px-2 text-sm font-normal text-neutral-subtle-bold lg:px-0">
        For the best experience, connect your ELD to verify your equipment
        <br v-if="!isTabletOrMobile" />
        and build trust and visibility among our community of brokers.
      </p>
    </div>
    <div
      :class="[
        'flex w-full gap-3',
        isTabletOrMobile
          ? 'flex-col-reverse px-2'
          : 'flex-row items-center justify-center',
      ]"
    >
      <button
        :class="[
          'rounded-md text-sm font-medium',
          isTabletOrMobile
            ? 'h-12 w-full bg-steel-20 text-neutral-subtle-bold'
            : 'h-10 bg-steel-20 px-3 text-neutral-subtle-bold hover:bg-hovered-gray active:bg-pressed-gray',
        ]"
        @click="onLearnMoreEld"
      >
        Learn more
      </button>

      <button
        :class="[
          'rounded-md text-sm font-medium',
          isTabletOrMobile
            ? 'h-12 w-full bg-neutral-subtle-yellow text-neutral-boldest'
            : 'h-10 bg-neutral-subtle-yellow px-3 text-neutral-boldest hover:bg-neutral-subtle-yellow-bold active:bg-neutral-subtle-yellow-boldest',
        ]"
        @click="onClickEld"
      >
        Connect ELD
      </button>
    </div>
  </div>
  <div v-else>
    <div>
      <div class="flex flex-col gap-6 rounded-md bg-white text-left">
        <!-- <div class="flex items-center justify-between"> -->
        <!-- <h3
            class="text-xl text-steel-1100"
            :class="{ hidden: isTabletOrMobile }"
          >
            ELD Connections
          </h3> -->
        <!-- <button
            v-if="!isCarrierVehiclesResponseLoading"
            :class="[
              'rounded-md bg-neutral-subtle-bold px-3 text-sm font-medium text-white hover:bg-neutral-blue active:bg-neutral-boldest',
              isTabletOrMobile ? 'mt-4 h-12 w-full' : 'h-10',
            ]"
            @click="onNewEldConnection"
          >
            New ELD Connection
          </button> -->
        <!-- </div> -->

        <div
          class="flex flex-col pb-14"
          :class="{ 'gap-4': isTabletOrMobile }"
        >
          <div
            class="flex flex-row overflow-x-auto"
            :class="isTabletOrMobile ? 'flex-wrap' : 'my-2'"
          >
            <EldProviderSummary
              v-for="provider in eldProvidersNotRevoked"
              :key="provider.id"
              :eld-provider="provider"
              :total-providers="eldProvidersNotRevoked?.length"
              :show-actions="true"
              :actions-trigger="
                eldProvidersNotRevoked.length > 1 ? 'menu' : 'refresh'
              "
              :loading="isCarrierVehiclesResponseLoading"
              @refresh="onRefresh"
              @delete="onDelete"
            />
          </div>
          <template v-if="!isTabletOrMobile">
            <EquipmentSkeletonTable v-if="isCarrierVehiclesResponseLoading" />
            <EquipmentTable
              v-else
              :data="carrierVehiclesForCarrier"
              :pagination="carrierVehiclePagination"
              :display-skeleton="true"
              :show-more-rows="true"
              @resolve-issues-click="handleResolveIssuesClick"
            />
          </template>
          <template v-else>
            <template v-if="isCarrierVehiclesResponseLoading">
              <EquipmentCardSkeleton
                v-for="i in 10"
                :key="i"
              />
            </template>
            <template v-else>
              <div
                class="flex w-full flex-wrap items-center justify-center gap-4 pb-16"
              >
                <EquipmentCard
                  v-for="(item, index) in carrierVehiclesForCarrier"
                  :key="index"
                  :equipment="item"
                  @resolve-issues-click="handleResolveIssuesClick"
                />
              </div>
              <div class="mt-10 flex items-center justify-center">
                <pagination
                  :selected-page="carrierVehiclePagination.currentPage"
                  :total-pages="carrierVehiclePagination.totalPages"
                  equivalent
                  @select-page="onSelectPage"
                ></pagination>
              </div>
            </template>
          </template>
        </div>
      </div>
    </div>
  </div>
  <TeleportModalWrapperComponent
    :margin-top="innerHeight < 700 ? 'mt-4' : ''"
    :align="innerHeight < 700 ? 'start' : 'center'"
    :is-modal-open="isCarrierWorkflowModalVisible"
    @close-modal="isCarrierWorkflowModalVisible = false"
  >
    <CarrierWorkflowModalComponent
      workflow-type="eldExempt"
      :equipment-vehicle="selectedVehicle"
      @close-modal="isCarrierWorkflowModalVisible = false"
    />
  </TeleportModalWrapperComponent>
</template>
